import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';
import { Modal } from './Modal.js';
import type { ScreenConfirm } from './ScreenLayout.js';

/**
 * LES FENETRES DE CONFIRMATION DU PRODUIT : une seule boite, deux tons.
 *
 * Elles n'appartiennent pas au mode Archives : le chantier 2_0 les ecrit, le
 * chantier 3_0 (selection de lignes et actions en LOT) les reprend TELLES
 * QUELLES en leur passant un NOMBRE au lieu d'une fiche ("Archiver 3 fiches ?",
 * "Supprimer definitivement 3 fiches ?"). C'est pourquoi les textes vivent dans
 * des fabriques (`archiveConfirm` / `removeConfirm`) et pas dans le JSX : deux
 * fenetres differentes pour un meme geste feraient deux vocabulaires, et le
 * lecteur chercherait ce qui change entre les deux au lieu de lire le nombre.
 *
 * DEUX TONS, ET UN SEUL EST ROUGE.
 * - ARCHIVER est neutre et REVERSIBLE : la fenetre dit ou PART la fiche, pas ce
 *   qu'on perd. Rien n'y est rouge, parce que rien n'y est definitif.
 * - SUPPRIMER est la SEULE FENETRE ROUGE DU PRODUIT. Son bouton de confirmation
 *   porte le seul aplat `--danger` PLEIN qu'on rencontrera : c'est le DERNIER
 *   geste d'un chemin, jamais le premier. Un aplat rouge propose en premier
 *   attire le clic qu'il devrait retenir, et c'est exactement pourquoi
 *   "Supprimer" reste SECONDAIRE dans le menu de fiche et dans la barre de lot.
 *
 * ANNULER EST A GAUCHE DU ROUGE (la main y arrive avant) ET PORTE LE FOCUS PAR
 * DEFAUT : la touche Entree annule au lieu de detruire. Pas de champ "tape le
 * nom pour confirmer" : trop lourd pour un usage quotidien, et ca n'apprend rien
 * de plus que la phrase (decision du fondateur).
 *
 * CE QU'ON N'ECRIT NULLE PART : qu'une ligne supprimee survit un temps en base.
 * C'est un garde-fou d'exploitation pour le support Taki, pas une corbeille
 * utilisateur. Pour l'utilisateur, supprime veut dire supprime.
 */

/** Ce que porte une fenetre de confirmation, une fois ses textes composes. */
export interface ConfirmDialogProps {
  /** Ouverte. Basculer suffit : le Modal anime l'entree ET la sortie. */
  open: boolean;
  /** `neutral` : rien de definitif. `danger` : le rouge plein, une seule fois. */
  tone: 'neutral' | 'danger';
  /** La question, en tete ("Archiver cette fiche ?"). */
  title: string;
  /** Le corps. Un tableau rend un paragraphe par entree. */
  body: string | string[];
  /** Libelle du bouton qui fait le geste ("Archiver"). */
  confirmLabel: string;
  /** Libelle de la sortie. Defaut "Annuler". */
  cancelLabel?: string;
  /** Le geste est confirme. L'appelant ferme la fenetre lui-meme. */
  onConfirm: () => void;
  /** Sortie : bouton Annuler, voile, ou Echap. */
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  tone,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Annuler',
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  /*
   * LE FOCUS PAR DEFAUT VA SUR ANNULER, et il faut le reprendre APRES le Modal.
   * Le Modal focalise son PANNEAU a l'ouverture (pour qu'Echap reponde tout de
   * suite), et les effets d'un enfant s'executent AVANT ceux de son parent :
   * poser le focus dans un effet ordinaire, c'est se le faire reprendre a la
   * ligne suivante. Une frame plus tard, le commit est fini et le panneau a
   * deja pris puis rendu la main. `autoFocus` ne marcherait pas davantage : il
   * se joue pendant le commit, donc avant l'effet du parent.
   */
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => cancelRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const paragraphs = Array.isArray(body) ? body : [body];

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="md"
      footer={
        /*
         * L'ORDRE DES DEUX BOUTONS EST LA MOITIE DU TRAVAIL. "Annuler" est a
         * GAUCHE du geste (la main y arrive avant) et il porte le focus par
         * defaut : dans une fenetre destructrice, la touche Entree doit sortir,
         * jamais detruire. Le pied du Modal aligne deja a droite, avec l'ecart
         * du systeme : rien a redessiner ici.
         */
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            className={cn(
              // LE SEUL --danger PLEIN DU PRODUIT. La variante `danger` du
              // socle est en CONTOUR (surface blanche, encre rouge) : c'est la
              // bonne forme partout ailleurs, et la mauvaise ICI, ou le geste
              // est deja choisi et ou le bouton doit porter la gravite de ce
              // qui suit. Le survol assombrit sans changer de teinte : le
              // systeme n'a pas de `--danger-hover`, et en fabriquer un serait
              // un jeton de plus pour un seul bouton du produit.
              tone === 'danger' &&
                'border-danger bg-danger text-surface hover:bg-danger hover:brightness-[0.92]',
            )}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {paragraphs.map((paragraph, index) => (
          <p
            key={index}
            className="text-[0.9375rem] leading-normal text-ink-900 [text-wrap:pretty]"
          >
            {paragraph}
          </p>
        ))}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* LES TEXTES, ET L'ACCORD QUI SUIT LE NOMBRE                                 */
/*                                                                            */
/* L'accord descend jusque DANS le corps ("Elle quitte les listes" / "Elles    */
/* quittent les listes") : une phrase au pluriel forcee sur une seule fiche    */
/* s'entend, et c'est exactement le genre de detail qui fait qu'une fenetre    */
/* de confirmation se lit comme un message et non comme un gabarit.           */
/* -------------------------------------------------------------------------- */

/**
 * La fenetre d'ARCHIVAGE. Neutre : elle dit ou PART la fiche, pas ce qu'on
 * perd. `count` vaut 1 depuis la fiche, N depuis la barre de lot (3_0).
 */
export function archiveConfirm(count = 1): ScreenConfirm {
  const many = count > 1;
  return {
    tone: 'neutral',
    title: many ? `Archiver ${count} fiches ?` : 'Archiver cette fiche ?',
    body: many
      ? 'Elles quittent les listes. Tu les retrouveras dans les archives.'
      : 'Elle quitte les listes. Tu la retrouveras dans les archives.',
    confirmLabel: 'Archiver',
  };
}

/**
 * La fenetre de SUPPRESSION, la seule rouge du produit. Elle dit sans detour
 * que c'est definitif, et ne promet AUCUN moyen de revenir en arriere.
 */
export function removeConfirm(count = 1): ScreenConfirm {
  const many = count > 1;
  return {
    tone: 'danger',
    title: many
      ? `Supprimer definitivement ${count} fiches ?`
      : 'Supprimer definitivement cette fiche ?',
    body: many
      ? "Elles seront effacees pour de bon, avec leur historique. C'est definitif : rien ne permettra de les retrouver."
      : "Elle sera effacee pour de bon, avec son historique. C'est definitif : rien ne permettra de la retrouver.",
    confirmLabel: 'Supprimer definitivement',
  };
}

/** Ce que prennent les deux fenetres nommees : un nombre, et les deux issues. */
export interface CountConfirmDialogProps {
  open: boolean;
  /** Nombre de fiches concernees. Defaut 1 (le geste depuis la fiche). */
  count?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * "Archiver cette fiche ?" / "Archiver 3 fiches ?". La forme que le chantier
 * 3_0 monte telle quelle depuis la barre de lot.
 */
export function ArchiveConfirmDialog({
  open,
  count = 1,
  onConfirm,
  onCancel,
}: CountConfirmDialogProps): JSX.Element {
  return <ConfirmDialog open={open} {...archiveConfirm(count)} onConfirm={onConfirm} onCancel={onCancel} />;
}

/**
 * "Supprimer definitivement cette fiche ?" / "... 3 fiches ?". Meme boite, meme
 * ordre de boutons, meme focus par defaut : c'est le MEME geste, il porte
 * seulement un nombre.
 */
export function RemoveConfirmDialog({
  open,
  count = 1,
  onConfirm,
  onCancel,
}: CountConfirmDialogProps): JSX.Element {
  return <ConfirmDialog open={open} {...removeConfirm(count)} onConfirm={onConfirm} onCancel={onCancel} />;
}

export default ConfirmDialog;
